"use client";

import { sbBlok } from "@/types/sb-types";
import BlogPost from "@/components/BlogPost";
import Cta from "./Cta";
import Link from "next/link";
import { motion } from "framer-motion";
import { useWindowSize } from "@/hooks/useWindowSize";
import clsx from "clsx";

interface BlogPostsOverviewProps {
  stories: sbBlok[];
  blok: sbBlok;
  isFirstPostLarge?: boolean;
}

const BlogPostsOverview = ({
  blok,
  stories,
  isFirstPostLarge,
}: BlogPostsOverviewProps) => {
  const { isDesktop, windowSize } = useWindowSize();

  const smallBlogPosts = isFirstPostLarge
    ? stories?.slice(1)
    : windowSize.width > 809 && windowSize.width <= 960
    ? stories?.slice(0, 2)
    : stories?.slice(0, 3);

  return (
    <div
      className={clsx("grid-container mt-7 xl:mt-[50px] 2xl:mt-28", {
        "pt-7 lg:pt-14":
          !!blok.spacing_top?.length && blok.spacing_top[0].option === "large",
        "pt-14 lg:pt-[120px]":
          !!blok.spacing_top?.length &&
          blok.spacing_top[0].option === "x_large",
        "pt-[120px] lg:pt-[180px]":
          !!blok.spacing_top?.length &&
          blok.spacing_top[0].option === "xx_large",
        "pb-7 lg:pb-14":
          !!blok.spacing_bottom?.length &&
          blok.spacing_bottom[0].option === "large",
        "pb-14 lg:pb-[120px]":
          !!blok.spacing_bottom?.length &&
          blok.spacing_bottom[0].option === "x_large",
        "pb-[120px] lg:pb-[180px]":
          !!blok.spacing_bottom?.length &&
          blok.spacing_bottom[0].option === "xx_large",
      })}
    >
      <div className="col-start-1 col-span-4 lg:col-start-2 lg:col-span-14 2xl:col-start-4 2xl:col-span-26">
        <h1 className="text-[--tm-fs-m] text-center mb-[10px]">Latest</h1>

        {isFirstPostLarge && (
          <BlogPost
            isLarge
            ctaType="button"
            publishedAt={stories[0].first_published_at}
            blok={{
              _uid: stories[0].content._uid,
              component: stories[0].content.component,
              image: stories[0].content.preview_image,
              meta_title: stories[0].content.preview_info_title,
              title: stories[0].content.preview_title,
              intro: stories[0].content.preview_intro,
              cta_link: blok?.cta_link
                ? [
                    {
                      name: blok.cta_link?.[0]?.name,
                      link: {
                        linktype: blok.cta_link?.[0]?.link?.linktype,
                        story: {
                          url: blok.cta_link?.[0]?.link?.story?.url,
                        },
                      },
                    },
                  ]
                : undefined,
            }}
          />
        )}

        {smallBlogPosts?.length > 0 && (
          <motion.section
            className="grid gap-6 mt-16 md:grid-cols-2 md:gap-4 lg:grid-cols-3 lg:grid-flow-col lg:mt-4 2xl:gap-12 2xl:mt-10"
            viewport={{ once: false }}
            initial={{
              opacity: isDesktop ? 1 : 0.3,
              transform: isDesktop ? "translate(0, 0)" : "translate(0, 60px)",
            }}
            whileInView={{
              opacity: 1,
              transform: "translate(0, 0)",
            }}
            transition={{
              ease: "linear",
              duration: 0.8,
            }}
          >
            {smallBlogPosts.map((blogPost: any) => (
              <Link
                key={blogPost.content._uid}
                href={blogPost.full_slug}
                className="flex w-full"
              >
                <BlogPost
                  publishedAt={blogPost.first_published_at}
                  blok={{
                    _uid: blogPost.content._uid,
                    component: blogPost.content.component,
                    image: blogPost.content.preview_image,
                    meta_title: blogPost.content.preview_info_title,
                    title: blogPost.content.preview_title,
                    intro: blogPost.content.preview_intro,
                  }}
                />
              </Link>
            ))}
          </motion.section>
        )}

        {!isFirstPostLarge && blok?.cta_link && (
          <motion.div
            viewport={{ once: false }}
            initial={{
              transform: isDesktop ? "translate(0, 0)" : "translate(0, 60px)",
            }}
            whileInView={{
              transform: "translate(0, 0)",
            }}
            transition={{
              ease: "linear",
              duration: 0.8,
            }}
          >
            <Cta
              className="mt-[20px] lg:mt-[35px] mx-auto"
              href={blok.cta_link[0]?.link?.url}
              fullSlug={blok.cta_link[0]?.link?.story?.full_slug}
              name={blok.cta_link[0]?.name}
            />
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BlogPostsOverview;
